import React from 'react';
import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '@lqa-frontend/ui-system';
import { BodyM } from '../typography';
import { Button } from '../buttons/Button';
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '../collapsible';

export interface ITimelineItem {
	id: number | string;
	date: string;
	content: React.ReactNode;
	icon?: React.ReactNode;
}

type TimelineRootProps = useRender.ComponentProps<'div'>;

function TimelineRoot({
	className,
	render,
	children,
	...props
}: TimelineRootProps) {
	const defaultProps = {
		'className': cn('group/timeline flex flex-col ps-80 ml-6', className),
		'data-slot': 'timeline',
		children,
	};

	return useRender({
		defaultTagName: 'div',
		render,
		props: mergeProps<'div'>(defaultProps, props),
	});
}

function TimelineContent({
	className,
	render,
	children,
	...props
}: useRender.ComponentProps<'div'>) {
	const defaultProps = {
		'className': cn(
			'text-14 leading-lh4 text-text-secondary ps-12 -translate-y-6',
			className
		),
		'data-slot': 'timeline-content',
		children,
	};

	return useRender({
		defaultTagName: 'div',
		render,
		props: mergeProps<'div'>(defaultProps, props),
	});
}

type TimelineDateProps = React.ComponentProps<typeof BodyM>;

function TimelineDate({
	className,
	children,
	color = 'secondary',
	...props
}: TimelineDateProps) {
	return (
		<BodyM
			color={color}
			className={cn(
				'mb-4 block max-sm:h-4 absolute -left-128 w-96 shrink-0 text-right whitespace-nowrap -translate-y-6',
				className
			)}
			data-slot="timeline-date"
			{...props}
		>
			{children}
		</BodyM>
	);
}

type TimelineIndicatorProps = useRender.ComponentProps<'div'>;

function TimelineIndicator({
	className,
	children,
	render,
	...props
}: TimelineIndicatorProps) {
	const hasCustomIcon = React.Children.toArray(children).some(
		(child) => child != null && typeof child !== 'boolean'
	);

	const defaultProps = {
		'aria-hidden': true,
		'className': cn(
			'-left-6 -translate-x-1/2 absolute top-0',
			hasCustomIcon
				? 'inline-flex items-center justify-center bg-transparent p-0 -translate-y-2'
				: 'size-8 rounded-max bg-zinc-500',
			className
		),
		'data-slot': 'timeline-indicator',
		children,
	};

	return useRender({
		defaultTagName: 'div',
		render,
		props: mergeProps<'div'>(defaultProps, props),
	});
}

export type ITimelineShorthandItemProps = {
	date: string;
	label: string;
	labelLink?: React.ReactNode;
	labelBadge?: React.ReactNode;
	description?: string;
	/**
	 * Shorthand only: when `description` is longer than this many characters, it is shown in a
	 * collapsible with show more / show less. Ignored when using custom `children`.
	 * @default 200
	 */
	maxCharactersBeforeCollapsing?: number;
	bottomButton?: React.ReactNode;
	/** Custom step marker (e.g. design-system `Icon`). Falls back to the default dot when omitted. */
	icon?: React.ReactNode;
	/** @deprecated Use `icon` instead. */
	indicator?: React.ReactNode;
};

export type ITimelineShorthandListItem = {
	id: ITimelineItem['id'];
} & ITimelineShorthandItemProps;

function TimelineRigidLongDescription({
	description,
	hasBottomButton,
	maxCharactersBeforeCollapsing,
}: {
	description: string;
	hasBottomButton: boolean;
	maxCharactersBeforeCollapsing: number;
}) {
	return (
		<Collapsible
			defaultOpen={false}
			className={cn('group', hasBottomButton ? 'mb-12' : undefined)}
		>
			<BodyM color="secondary" className="group-data-[state=open]:hidden">
				{description.slice(0, maxCharactersBeforeCollapsing)}…
			</BodyM>
			<CollapsibleContent>
				<BodyM color="secondary" className="mb-8">
					{description}
				</BodyM>
			</CollapsibleContent>
			<CollapsibleTrigger asChild>
				<Button
					variant="link"
					size="m"
					className="hover:underline p-0 hover:bg-transparent active:bg-transparent"
				>
					<span className="group-data-[state=open]:hidden">Show more</span>
					<span className="hidden group-data-[state=open]:inline">
						Show less
					</span>
				</Button>
			</CollapsibleTrigger>
		</Collapsible>
	);
}

type TimelineCompoundItemProps = useRender.ComponentProps<'div'> &
	Partial<ITimelineShorthandItemProps>;

function TimelineCompoundItem({
	className,
	render,
	children,
	date,
	label,
	labelLink,
	labelBadge,
	description,
	maxCharactersBeforeCollapsing = 200,
	bottomButton,
	icon,
	indicator,
	...props
}: TimelineCompoundItemProps) {
	const hasCustomChildren = React.Children.toArray(children).some(
		(child) => child != null && typeof child !== 'boolean'
	);

	const defaultProps = {
		'className': cn(
			'group/timeline-item relative flex flex-1 flex-col gap-2 ms-32 not-last:pb-24',
			className
		),
		'data-slot': 'timeline-item',
		'children': hasCustomChildren ? (
			children
		) : (
			<>
				<TimelineSeparator />
				{date ? <TimelineDate>{date}</TimelineDate> : null}
				<TimelineIndicator>{icon ?? indicator}</TimelineIndicator>
				<TimelineContent>
					<div>
						<div
							className={cn(
								'flex items-center gap-6',
								labelLink ? '-mt-8' : undefined,
								description && !labelLink ? 'mb-8' : undefined
							)}
						>
							{labelBadge ? (
								<span className="shrink-0">{labelBadge}</span>
							) : null}
							{label ? (
								<BodyM hasEllipsis className="min-w-0 flex-1">
									{label}
								</BodyM>
							) : null}
							{labelLink ? <span className="shrink-0">{labelLink}</span> : null}
						</div>

						{description ? (
							typeof description === 'string' &&
							description.length > maxCharactersBeforeCollapsing ? (
								<TimelineRigidLongDescription
									description={description}
									hasBottomButton={Boolean(bottomButton)}
									maxCharactersBeforeCollapsing={maxCharactersBeforeCollapsing}
								/>
							) : (
								<BodyM
									color="secondary"
									className={cn(bottomButton ? 'mb-12' : undefined)}
								>
									{description}
								</BodyM>
							)
						) : null}

						{bottomButton}
					</div>
				</TimelineContent>
			</>
		),
	};

	return useRender({
		defaultTagName: 'div',
		render,
		props: mergeProps<'div'>(defaultProps, props),
	});
}

function TimelineSeparator({
	className,
	render,
	children,
	...props
}: useRender.ComponentProps<'div'>) {
	const defaultProps = {
		'aria-hidden': true,
		'className': cn(
			'-left-6 -translate-x-1/2 absolute self-start bg-zinc-500 group-last/timeline-item:hidden h-[calc(100%-1rem)] w-px translate-y-12',
			className
		),
		'data-slot': 'timeline-separator',
		children,
	};

	return useRender({
		defaultTagName: 'div',
		render,
		props: mergeProps<'div'>(defaultProps, props),
	});
}

function TimelineTitle({
	className,
	render,
	children,
	...props
}: useRender.ComponentProps<'h3'>) {
	const defaultProps = {
		'className': cn(
			'text-14 font-medium leading-lh4 text-text-primary',
			className
		),
		'data-slot': 'timeline-title',
		children,
	};

	return useRender({
		defaultTagName: 'h3',
		render,
		props: mergeProps<'h3'>(defaultProps, props),
	});
}

export const Timeline = TimelineRoot as typeof TimelineRoot & {
	Item: typeof TimelineCompoundItem;
	Content: typeof TimelineContent;
	Date: typeof TimelineDate;
	Indicator: typeof TimelineIndicator;
	Separator: typeof TimelineSeparator;
	Title: typeof TimelineTitle;
};

Timeline.Item = TimelineCompoundItem;
Timeline.Content = TimelineContent;
Timeline.Date = TimelineDate;
Timeline.Indicator = TimelineIndicator;
Timeline.Separator = TimelineSeparator;
Timeline.Title = TimelineTitle;
